import { Controller } from "@hotwired/stimulus"

// Connects to data-controller="dropdown"
export default class extends Controller {
  static targets = ["menu", "button"]
  static values = { fixed: { type: Boolean, default: false } }

  connect() {
    // Close dropdown when clicking outside
    this._closeOnOutsideClick = this.closeOnOutsideClick.bind(this)
    this._closeOnEscape = this.closeOnEscape.bind(this)
    this._closeOnScroll = this.closeOnScroll.bind(this)

    document.addEventListener("click", this._closeOnOutsideClick)
    document.addEventListener("keydown", this._closeOnEscape)
  }

  disconnect() {
    document.removeEventListener("click", this._closeOnOutsideClick)
    document.removeEventListener("keydown", this._closeOnEscape)
    this._removeScrollListener()
  }

  toggle(event) {
    event.preventDefault()
    event.stopPropagation()
    
    if (this.isOpen()) {
      this.close()
    } else {
      this.open()
    }
  }

  open() {
    // Close other open dropdowns first
    this.closeAllDropdowns()

    if (this.fixedValue) {
      this._positionFixed()
      this._addScrollListener()
    }

    // Support both visibility and hidden class approaches
    this.menuTarget.classList.remove("opacity-0", "invisible", "hidden")
    this.menuTarget.classList.add("opacity-100", "visible")
    if (this.hasButtonTarget) {
      this.buttonTarget.setAttribute("aria-expanded", "true")
    }
  }

  close() {
    // Support both visibility and hidden class approaches
    this.menuTarget.classList.remove("opacity-100", "visible")
    this.menuTarget.classList.add("opacity-0", "invisible", "hidden")
    if (this.hasButtonTarget) {
      this.buttonTarget.setAttribute("aria-expanded", "false")
    }

    if (this.fixedValue) {
      this._resetFixedPosition()
      this._removeScrollListener()
    }
  }

  isOpen() {
    return this.menuTarget.classList.contains("opacity-100") ||
           (this.menuTarget.classList.contains("visible") && !this.menuTarget.classList.contains("hidden"))
  }

  closeOnOutsideClick(event) {
    // Don't close if clicking on the dropdown itself or its button
    if (this.element.contains(event.target)) {
      return
    }
    
    if (this.isOpen()) {
      this.close()
    }
  }

  closeOnEscape(event) {
    if (event.key === "Escape" && this.isOpen()) {
      this.close()
    }
  }

  // Close all other dropdowns (useful when multiple dropdowns exist)
  closeAllDropdowns() {
    document.querySelectorAll('[data-controller*="dropdown"]').forEach(element => {
      if (element !== this.element) {
        const controller = this.application.getControllerForElementAndIdentifier(element, "dropdown")
        if (controller && controller.isOpen()) {
          controller.close()
        }
      }
    })
  }

  // Fixed positioning helpers for dropdowns inside overflow containers
  _positionFixed() {
    const button = this.hasButtonTarget ? this.buttonTarget : this.element.querySelector("button")
    if (!button) return

    const rect = button.getBoundingClientRect()
    const menu = this.menuTarget

    menu.style.position = "fixed"
    menu.style.zIndex = "9999"

    // Position below the button, aligned to the right edge
    menu.style.top = `${rect.bottom + 4}px`
    menu.style.right = `${window.innerWidth - rect.right}px`
    menu.style.left = "auto"
  }

  _resetFixedPosition() {
    const menu = this.menuTarget
    menu.style.position = ""
    menu.style.zIndex = ""
    menu.style.top = ""
    menu.style.right = ""
    menu.style.left = ""
  }

  _addScrollListener() {
    // Find the nearest scrollable ancestor and close on scroll
    this._scrollParent = this._findScrollParent(this.element)
    if (this._scrollParent) {
      this._scrollParent.addEventListener("scroll", this._closeOnScroll, { passive: true })
    }
    window.addEventListener("scroll", this._closeOnScroll, { passive: true })
  }

  _removeScrollListener() {
    if (this._scrollParent) {
      this._scrollParent.removeEventListener("scroll", this._closeOnScroll)
      this._scrollParent = null
    }
    window.removeEventListener("scroll", this._closeOnScroll)
  }

  _findScrollParent(element) {
    let parent = element.parentElement
    while (parent) {
      const style = window.getComputedStyle(parent)
      if (style.overflow === "auto" || style.overflow === "scroll" ||
          style.overflowY === "auto" || style.overflowY === "scroll") {
        return parent
      }
      parent = parent.parentElement
    }
    return null
  }

  closeOnScroll() {
    if (this.isOpen()) {
      this.close()
    }
  }
}